import { Badge, Box, Button, Divider, Drawer, IconButton, List, Stack, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar, Scrollbar, useBoolean, varHover } from '@zeta-core';
import { AxiosResponse } from 'axios';
import { m } from 'framer-motion';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { MarkAsReadIcon, NotificationsCloseIcon, NotificationsIcon } from '@zeta-core/assets';
import { getErrorMessage, NotificationService, UserServiceAPIs } from '@zeta-services';
import {
  IGenericErrorResponse,
  INotificationAppView,
  INotificationAppViewResponse,
  INotificationsAppViewRequest,
  IUpdateUserResetPasswordRequestRequest,
} from '@zeta-types';
import { RouterLinks } from '../../app-route-paths';
import NotificationItem from './notifications-item';

interface INotificationsPopover {
  renderTabs: (theme: Theme) => JSX.Element;
  notificationsCount: number | undefined;
  currentTab: string;
  setMarkedAsRead: Dispatch<SetStateAction<boolean>>;
  permissions: {
    accessible: boolean;
    hasReadPermission: boolean;
    hasWritePermission: boolean;
    permissions: { type: string; statusCondition: string }[];
  };
}

const NotificationsPopover = ({ renderTabs, notificationsCount, currentTab, setMarkedAsRead, permissions }: INotificationsPopover) => {
  const navigate = useNavigate();
  const drawer = useBoolean();
  const theme = useTheme();
  const [notifications, setNotifications] = useState<INotificationAppView[] | []>([]);
  const [notificationsData, setNotificationsData] = useState<INotificationAppViewResponse | null>({});

  const updateUserResetPasswordRequestAPI = useMutation({
    mutationFn: (payload: IUpdateUserResetPasswordRequestRequest) => UserServiceAPIs().updateUserResetPasswordRequest(payload),

    onSuccess: () => {
      const payload: INotificationsAppViewRequest = {
        status: [],
        desc: true,
        skipPagination: true,
        isTotalCountRequired: true,
      };

      setTimeout(() => {
        notificationAppViewAPI.mutate(payload);
      }, 5000);
    },
  });

  const markRead = useMutation({
    mutationFn: (payload: { notificationIDs: Array<string>; isReadAll: boolean }) => NotificationService().markNotificationRead(payload),

    onSuccess: () => {
      const payload: INotificationsAppViewRequest = {
        status: [],
        desc: true,
        skipPagination: true,
        isTotalCountRequired: true,
      };

      notificationAppViewAPI.mutate(payload);
      setMarkedAsRead(true);
    },

    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>
      {!!notificationsCount && permissions.hasWritePermission && (
        <Tooltip title="Mark all as read">
          <Button
            type="text"
            color="primary"
            onClick={() => {
              markRead.mutateAsync({ notificationIDs: [], isReadAll: true });
            }}
          >
            <IconButton color="primary">
              <MarkAsReadIcon />
            </IconButton>
            <Typography variant="body2">Mark all as read</Typography>
          </Button>
        </Tooltip>
      )}
      <IconButton onClick={drawer.onFalse}>
        <NotificationsCloseIcon />
      </IconButton>
    </Stack>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding sx={{ height: '74vh', mb: 6 }}>
        {notifications?.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            updateUserResetPasswordRequestAPI={updateUserResetPasswordRequestAPI}
            markRead={markRead}
          />
        ))}
      </List>
    </Scrollbar>
  );

  const notificationAppViewAPI = useMutation({
    mutationFn: (payload: INotificationsAppViewRequest) => NotificationService().notificationAppView(payload),

    onSuccess: (response: AxiosResponse<INotificationAppViewResponse>) => {
      const { data: responseData } = response;
      setNotificationsData(responseData);

      if (currentTab === 'unread') {
        const unread = responseData?.response?.filter((x) => x.notificationStatus === 'Unread');
        setNotifications(unread || []);
      }

      if (currentTab === 'read') {
        const read = responseData?.response?.filter((x) => x.notificationStatus === 'Read');
        setNotifications(read || []);
      }

      if (currentTab === 'all') {
        setNotifications(responseData?.response);
      }
    },
  });

  useEffect(() => {
    if (currentTab === 'unread') {
      const unread = notificationsData?.response?.filter((x) => x.notificationStatus === 'Unread');
      setNotifications(unread || []);
    }

    if (currentTab === 'read') {
      const read = notificationsData?.response?.filter((x) => x.notificationStatus === 'Read');
      setNotifications(read || []);
    }

    if (currentTab === 'all') {
      setNotifications(notificationsData?.response);
    }
  }, [currentTab]);

  useEffect(() => {
    const payload: INotificationsAppViewRequest = {
      status: [],
      desc: true,
      skipPagination: true,
      isTotalCountRequired: true,
    };

    notificationAppViewAPI.mutate(payload);
  }, []);

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={notificationsCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },

          paper: {
            sx: { width: 1, maxWidth: 420 },
          },
        }}
      >
        {renderHead}
        <Divider />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 2.5, pr: 1, background: theme.palette.grey[200] }}>
          {renderTabs(theme)}
        </Stack>
        <Divider />
        {renderList}
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="large"
            onClick={() => {
              drawer.onFalse();
              navigate(RouterLinks.notificationsProfileSetup);
            }}
          >
            View All
          </Button>
        </Box>
      </Drawer>
    </>
  );
};
export default NotificationsPopover;
